import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { get } from "lodash";

import CSSModules from "lib/utils/css-modules";
import { Button, MainView, SubBar, Message, ApiService } from "lib/index";
import { Link } from "lib/components";
import { FormControl } from "lib/components/index";
import Calendar from "./Calendar";
import CalendarForm from "./CalendarForm";
import {
  stateSelector,
  initializeView,
  changeStartDate,
  getEndDate,
  displayedDays,
  groupSlotsByProductVariantId,
  getProductDetails,
} from "./index";
import {
  stateSelector as commonStateSelector,
  initializeView as initializeCommonView,
  getMinDate,
  getMaxDate,
  dateFormat,
} from "../common";
import styles from "./AccommodationCalendarView.less";
import ProductScheduleSlotPricingsRepository from "../../../lib/repositories/ProductScheduleSlotPricingsRepository";
import st from "../../../lib/utils";

@connect(
  (state) => ({
    currentCompanyId: state.auth.company.model.id,
    currentCompany: state.auth.company.model,
    products:
      commonStateSelector(state).products &&
      commonStateSelector(state).products.model &&
      commonStateSelector(state).products.model.records,
    pricingPlans: commonStateSelector(state).pricingPlans && commonStateSelector(state).pricingPlans.model,
    slots: stateSelector(state).slots && stateSelector(state).slots.model,
    slotsLoading: stateSelector(state).slots && stateSelector(state).slots.fetchLoading,
    fetchDone:
      commonStateSelector(state).products &&
      commonStateSelector(state).products.fetchDone &&
      commonStateSelector(state).pricingPlans &&
      commonStateSelector(state).pricingPlans.fetchDone,
    fetchLoading:
      (commonStateSelector(state).products && commonStateSelector(state).products.fetchLoading) ||
      (commonStateSelector(state).pricingPlans && commonStateSelector(state).pricingPlans.fetchLoading),
    fetchFailed:
      (commonStateSelector(state).products && commonStateSelector(state).products.fetchFailed) ||
      (commonStateSelector(state).pricingPlans && commonStateSelector(state).pricingPlans.fetchFailed),
    startDate: stateSelector(state).startDate,
    endDate: stateSelector(state).endDate,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        initializeView,
        initializeCommonView,
        changeStartDate,
      },
      dispatch
    )
)
@CSSModules(styles)
export default class AccommodationCalendarView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selected: {
        availabilities: [],
        prices: [],
      },
      editorData: {
        price: null,
        totalStockLeft: null,
        availabilityType: null,
        minNightStay: null,
      },
      editorSubmitLoading: false,
      editorResponseError: null,
      selectedProductsDetails: [],
    };
    this.editorSubmit$ = null;
    this.api = ApiService;
  }

  componentDidMount() {
    const { initializeView, initializeCommonView } = this.props;
    initializeView();
    initializeCommonView();
  }

  componentDidUpdate() {
    const { selected, selectedProductsDetails } = this.state;

    if (selected.availabilities.length > 0) {
      const productIdsByProductVariantIds = this.getProductIdsByProductVariantIds();
      const productsVariantIds = selected.availabilities.map(({ productVariantId }) => productVariantId);
      const uniqueVariantIds = [...new Set(productsVariantIds)];
      const selectedProducts = uniqueVariantIds.map((variantId) => {
        return {
          productId: productIdsByProductVariantIds[variantId],
          varaintId: variantId,
        };
      });

      const shouldFetchNewProducts = selectedProducts.map((product) => {
        return !selectedProductsDetails.some((item) => item.productId === product.productId);
      });

      if (shouldFetchNewProducts.includes(true)) {
        getProductDetails(selectedProducts).map((response) => {
          response.then((data) => {
            data.forEach((obj) => {
              if (!selectedProductsDetails.some((item) => item.productId === obj.productId)) {
                selectedProductsDetails.push(obj);
              }
            });
          });
        });
      }
    }
  }

  componentWillUnmount() {
    if (this.editorSubmit$) {
      this.editorSubmit$.unsubscribe();
    }
  }

  retryFetching() {
    const { fetchProducts, fetchPricingPlans } = this.props;
    fetchProducts();
    fetchPricingPlans();
  }

  handleStartDateChange(nextStartDate) {
    this.props.changeStartDate(nextStartDate);
  }

  handleEditorDataChange(nextEditorData) {
    this.setState({ editorData: nextEditorData });
  }

  handleSelectedChange(availabilities, prices) {
    this.setState({
      selected: { availabilities, prices },
    });
  }

  handleEditorDataSave(e) {
    e.preventDefault();
    const { currentCompanyId } = this.props;
    const { selected, editorData, selectedProductsDetails } = this.state;
    const productIdsByProductVariantIds = this.getProductIdsByProductVariantIds();
    const currentSlotsByProductVariantId = groupSlotsByProductVariantId(this.props.slots);

    const slots = [];
    const groupedAvailabilities = {};
    if (
      editorData.availabilityType !== null ||
      editorData.totalStockLeft !== null ||
      editorData.minNightStay !== null
    ) {
      selected.availabilities.forEach(({ productVariantId, date }) => {
        const currentProduct = selectedProductsDetails
          .map((item) => {
            if (item.varaintId === productVariantId) {
              return {
                ...item,
                availabilityType: item.default_requires_company_approval
                  ? "available_with_company_approval"
                  : "available_with_instant_booking",
              };
            }
          })
          .filter((item) => item !== undefined);
        const slot = {
          starts_at: moment(date).format("DD-MM-YYYY"),
          ends_at: moment(date).add(1, "day").format("DD-MM-YYYY"),
          company_id: currentCompanyId,
          product_id: productIdsByProductVariantIds[productVariantId],
          product_variant_id: productVariantId,
          ...(editorData.availabilityType !== null
            ? { availability_type: editorData.availabilityType }
            : { availability_type: currentProduct[0].availabilityType }),
          ...(editorData.totalStockLeft !== null ? { total_stock_left: editorData.totalStockLeft } : {}),
          ...(editorData.minNightStay !== null ? { min_night_stay: editorData.minNightStay } : {}),
        };
        slots.push(slot);
        if (!(productVariantId in groupedAvailabilities)) {
          groupedAvailabilities[productVariantId] = {};
        }
        if (!(date in groupedAvailabilities[productVariantId])) {
          groupedAvailabilities[productVariantId][date] = slot;
        }
      });
    }

    const pricings = [];
    if (editorData.price !== null) {
      selected.prices.forEach(({ productVariantId, pricingPlanId, date }) => {
        const currentProduct = selectedProductsDetails
          .map((item) => {
            if (item.varaintId === productVariantId) {
              return {
                ...item,
                availabilityType: item.default_requires_company_approval
                  ? "available_with_company_approval"
                  : "available_with_instant_booking",
              };
            }
          })
          .filter((item) => item !== undefined);

        pricings.push({
          date: moment(date).format("DD-MM-YYYY"),
          product_variant_id: productVariantId,
          pricing_plan_id: pricingPlanId,
          price_cents: editorData.price * 100,
        });
        if (
          !get(groupedAvailabilities, `${productVariantId}.${date}`) &&
          !get(currentSlotsByProductVariantId, `${productVariantId}.${date}`)
        ) {
          slots.push({
            starts_at: moment(date).format("DD-MM-YYYY"),
            ends_at: moment(date).add(1, "day").format("DD-MM-YYYY"),
            company_id: currentCompanyId,
            product_id: productIdsByProductVariantIds[productVariantId],
            product_variant_id: productVariantId,
            ...(editorData.availabilityType !== null
              ? { availability_type: editorData.availabilityType }
              : { availability_type: currentProduct[0].availabilityType }),
            ...(editorData.totalStockLeft !== null ? { total_stock_left: editorData.totalStockLeft } : {}),
            ...(editorData.minNightStay !== null ? { min_night_stay: editorData.minNightStay } : {}),
          });
        }
      });
    }

    this.processRequest(slots, pricings);
  }

  processRequest(slots, pricings) {
    if (this.editorSubmit$) {
      this.editorSubmit$.unsubscribe();
    }
    this.setState({ editorSubmitLoading: true });
    this.editorSubmit$ = ProductScheduleSlotPricingsRepository.bulkUpdate(slots, pricings).subscribe(
      () => {
        this.setState({ editorSubmitLoading: false }, () => {
          this.props.changeStartDate(this.props.startDate);
          this.handleSelectedChange([], []);
        });
      },
      (error) => {
        this.setState({ editorSubmitLoading: false, editorResponseError: st.api.findResponseErrorMessage(error) });
      }
    );
  }

  handleShowPreviousDays() {
    const { startDate: currentStartDate, changeStartDate } = this.props;
    let nextStartDate = moment(currentStartDate).subtract(displayedDays, "days").format(dateFormat);
    const minDate = getMinDate();
    if (nextStartDate < minDate) {
      nextStartDate = minDate;
    }
    changeStartDate(nextStartDate);
    this.handleSelectedChange([], []);
  }

  handleShowNextDays() {
    const { endDate: currentEndDate, changeStartDate } = this.props;
    let nextStartDate = moment(currentEndDate).add(1, "days").format(dateFormat);
    const nextEndDate = getEndDate(nextStartDate);
    const maxDate = getMaxDate();
    if (nextEndDate > maxDate) {
      nextStartDate = moment(maxDate)
        .subtract(displayedDays - 1, "days")
        .format(dateFormat);
    }
    changeStartDate(nextStartDate);
    this.handleSelectedChange([], []);
  }

  canShowPastDays() {
    const { startDate: currentStartDate } = this.props;
    const nextStartDate = moment(currentStartDate).subtract(displayedDays, "days");
    const nextEndDate = getEndDate(nextStartDate);
    return moment(nextEndDate) >= moment(getMinDate());
  }

  canShowNextDays() {
    const { endDate: currentEndDate } = this.props;
    const nextStartDateObj = moment(currentEndDate).add(1, "days");
    return nextStartDateObj < moment(getMaxDate());
  }

  getProductIdsByProductVariantIds() {
    return this.props.products.reduce((obj, product) => {
      (product.product_variants || []).forEach((pv) => {
        obj[pv.id] = product.id;
      });
      return obj;
    }, {});
  }

  render() {
    const {
      currentCompanyId,
      products,
      pricingPlans,
      slots,
      slotsLoading,
      fetchDone,
      fetchLoading,
      fetchFailed,
      retryFetching,
      startDate,
      endDate,
      currentCompany,
    } = this.props;
    const { selected, editorData, editorSubmitLoading, editorResponseError } = this.state;
    const showCalendarFormAvailability = Boolean(selected.availabilities.length);
    const showCalendarFormPrice = Boolean(selected.prices.length);

    return (
      <MainView.Container>
        <SubBar>
          <div className="u-displayFlex">
            <h1 className="u-flexGrow">{I18n.t("papp.accommodation.AccommodationCalendarView.title")}</h1>
            <div styleName="calendar-navigation">
              <FormControl.Date
                value={startDate}
                onChange={this.handleStartDateChange.bind(this)}
                isOutsideRange={(date) =>
                  date < moment(moment(getMinDate()).format(dateFormat)) ||
                  moment(moment(date).format(dateFormat)) > moment(getMaxDate())
                }
              />
              <Button.Group basic>
                <Button icon onClick={this.handleShowPreviousDays.bind(this)} disabled={!this.canShowPastDays()}>
                  <i className="material-icons icon">chevron_left</i>
                </Button>
                <Button icon onClick={this.handleShowNextDays.bind(this)} disabled={!this.canShowNextDays()}>
                  <i className="material-icons icon">chevron_right</i>
                </Button>
              </Button.Group>
              <Button
                as={Link}
                to={`/c/${currentCompanyId}/accommodation/scheduler`}
                content={I18n.t("papp.accommodation.AccommodationCalendarView.schedulerLink")}
              />
            </div>
          </div>
        </SubBar>
        <MainView.Content hasSubBar padded={false}>
          <MainView.Dimmer loading={fetchLoading} failed={fetchFailed} retry={retryFetching} />
          {fetchDone &&
            ((products || []).length ? (
              <div>
                <Calendar
                  {...{ startDate, endDate, products, pricingPlans, currentCompany }}
                  slots={slots || []}
                  loading={slotsLoading}
                  onSelectedChange={this.handleSelectedChange.bind(this)}
                  isFormOpen={showCalendarFormAvailability || showCalendarFormPrice}
                />
                {(showCalendarFormAvailability || showCalendarFormPrice) && (
                  <CalendarForm
                    data={editorData}
                    currency={currentCompany.default_product_price_currency}
                    showAvailability={showCalendarFormAvailability}
                    showPrice={showCalendarFormPrice}
                    onChange={this.handleEditorDataChange.bind(this)}
                    onSubmit={this.handleEditorDataSave.bind(this)}
                    loading={editorSubmitLoading}
                    responseError={editorResponseError}
                  />
                )}
              </div>
            ) : (
              <Message warning compact>
                {I18n.t("papp.accommodation.AccommodationSchedulerView.noProductsInfo")}
              </Message>
            ))}
        </MainView.Content>
      </MainView.Container>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/accommodation/AccommodationCalendarView/AccommodationCalendarView.jsx